import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Building2, MapPin, Users, Plus, Edit, ArrowRight, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function MyTeamPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", location: "", team_type: "club", logo_url: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teams/mine");
        setTeam(data && data.id ? data : null);
      } catch { toast.error("Failed to load your team"); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Team name required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/teams", form);
      toast.success("Team created!");
      setTeam(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create team"); }
    finally { setCreating(false); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-12 text-center text-zinc-400 text-sm">Loading...</div>;

  if (team) {
    const isOwner = team.owner_id === (user?._id || user?.id);
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="my-team-view">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>My Team</h1>
          <Button asChild variant="outline" className="border-[#E6E6E6] gap-1.5 text-xs h-8" data-testid="browse-teams-btn">
            <Link to="/teams"><Trophy size={12} /> Browse All Teams</Link>
          </Button>
        </div>

        <div className="bg-white border border-[#E6E6E6] rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#074572] to-[#053a60] h-20" />
          <div className="px-5 pb-5 -mt-10">
            <div className="w-20 h-20 rounded-lg bg-white border-4 border-white shadow-sm flex items-center justify-center overflow-hidden">
              {team.logo_url ? (
                <img src={team.logo_url} alt={team.name} className="w-full h-full object-cover" />
              ) : (
                <Building2 size={32} className="text-[#074572]" />
              )}
            </div>
            <h2 className="text-xl font-semibold text-zinc-900 mt-3" style={{ fontFamily: 'Outfit' }}>{team.name}</h2>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge className="text-[10px] bg-[#074572]/5 text-[#074572] capitalize">{team.team_type}</Badge>
              {team.location && <span className="text-xs text-zinc-500 flex items-center gap-0.5"><MapPin size={11} />{team.location}</span>}
              <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Users size={11} />{team.members?.length || 0} members</span>
            </div>

            {team.description && <p className="text-sm text-zinc-600 mt-4 leading-relaxed">{team.description}</p>}

            <div className="flex items-center gap-2 mt-5 flex-wrap">
              <Button onClick={() => navigate(`/teams/${team.id}`)} className="bg-[#074572] hover:bg-[#053a60] text-white gap-1.5" data-testid="open-team-page-btn">
                Open Team Page <ArrowRight size={14} />
              </Button>
              {isOwner && (
                <Button variant="outline" className="border-[#E6E6E6] gap-1.5" onClick={() => navigate(`/teams/${team.id}?edit=1`)} data-testid="edit-team-btn">
                  <Edit size={13} /> Edit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No team — show creation form
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="create-team-view">
      <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>Create Your Team</h1>
      <p className="text-sm text-zinc-500 mt-1 mb-6">Set up your club, academy or national team page. It will appear on your profile and in the global Teams directory.</p>

      <form onSubmit={handleCreate} className="bg-white border border-[#E6E6E6] rounded-lg p-5 space-y-4">
        <div>
          <Label className="text-zinc-700 text-sm">Team Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. PAOK FC, Makedonikos FC" className="mt-1.5 border-[#E6E6E6]" data-testid="team-name-input" required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-700 text-sm">Type</Label>
            <Select value={form.team_type} onValueChange={(v) => setForm({ ...form, team_type: v })}>
              <SelectTrigger className="mt-1.5 border-[#E6E6E6]" data-testid="team-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="club">Club</SelectItem>
                <SelectItem value="academy">Academy</SelectItem>
                <SelectItem value="national">National Team</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-700 text-sm">Location</Label>
            <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Thessaloniki, Greece" className="mt-1.5 border-[#E6E6E6]" data-testid="team-location-input" />
          </div>
        </div>
        <div>
          <Label className="text-zinc-700 text-sm">Logo URL (optional)</Label>
          <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." className="mt-1.5 border-[#E6E6E6]" data-testid="team-logo-input" />
        </div>
        <div>
          <Label className="text-zinc-700 text-sm">Description</Label>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell the community about your team..." className="mt-1.5 border-[#E6E6E6] min-h-[80px]" data-testid="team-desc-input" />
        </div>
        <Button type="submit" disabled={creating} className="w-full bg-[#F85037] hover:bg-[#e04530] text-white gap-2" data-testid="team-submit-btn">
          <Plus size={16} /> {creating ? "Creating..." : "Create Team"}
        </Button>
      </form>
    </div>
  );
}
